export type HeroIcon = React.ForwardRefExoticComponent<
  Omit<React.SVGProps<SVGSVGElement>, "ref"> & {
    title?: string;
    titleId?: string;
  } & React.RefAttributes<SVGSVGElement>
>

export type Response = {
  question_id: number
  option_choice_id: number
}

export type ResponseData = {
  responses: Response[]
}

export type OptionChoice = {
  option_choice_id: number
  option_choice_name: string
  option_group_id: number,
  option_group_index: number,
  likert_score: number
}

export type Question = {
  input_type_id: number
  option_group_id: number
  page_number: number
  question_id: number
  question_title: string
}

export type User = {
  survey_key: string | null
  languge_id: number | null
  age_id: number | null
  years_experience_id: number | null
  gender_id: number | null
}

export type Admin = {
  email: string | null
}

export type CompanyResource = {
  company_resource_id: number,
  company_id: number,
  company_resource_name: string,
  company_resource_description: string | null,
  email: string | null,
  phone: string | null,
  link: string | null,
  link_text: string | null
}

export type Persona = {
  test_persona_id: number,
  name: string
}

//// Results /////
export type ProcessedResponse = {
  question_id: number,
  section_id: number,
  domain: FiveDomain,
  factor: FiveDomainFactor,
  value: number
}

export type ResultQuestion = {
  id: string;
  text: string;
  performanceLevel: "Strong" | "Good" | "Needs Work";
  category: "Thriving" | "Stable" | "Needs Focus";
  value: number;
}

export type Factor = {
  name: string;
  performanceLevel: "Strong" | "Good" | "Needs Work";
  category: "Thriving" | "Stable" | "Needs Focus";
  questions: ResultQuestion[];
}

export type DomainResult = {
  domain: FiveDomain;
  performanceLevel: "Strong" | "Good" | "Needs Work";
  category: "Thriving" | "Stable" | "Needs Focus";
  factors: Factor[];
}

export const BYU_COMPANY_IDS = [98, 99, 100];

export enum FiveDomain {
  MEANINGFUL_WORK = 1,
  SPIRITUALITY = 2,
  RELATIONSHIPS = 3,
  HOBBIES_AND_INTERESTS = 4,
  PHYSICAL_HEALTH = 5
}

export enum FiveDomainFactor {
  UNDERSTAND_ROLES_AND_RESPONSIBILITIES = 1,
  HOW_SUCCESS_IS_MEASURED = 2,
  GIVING_AND_RECEIVING_FEEDBACK = 3,
  MISSION = 4,
  OPERATIONS_ALIGN = 5,
  VALUES = 6,
  POSITIVE_STRESS_INDUCING = 7,
  PERFORMANCE_BASED = 8,
  ACCOUNTABILITY_CENTRIC = 9,
  RESULT_ORIENTED = 10,
  SENSE_OF_UNITY_IN_RELATIONSHIPS = 11,
  HAVE_HOBBIES_AND_UNDERSTAND_IMPORTANCE = 12,
  SLEEP = 13,
  DIET = 14,
  EXERCISE = 15,
  HYDRATION = 16
}

export const FIVE_DOMAIN_LABELS: Record<FiveDomain, string> = {
  [FiveDomain.MEANINGFUL_WORK]: "Meaningful Work",
  [FiveDomain.SPIRITUALITY]: "Clinical Spirituality",
  [FiveDomain.RELATIONSHIPS]: "Relationships",
  [FiveDomain.HOBBIES_AND_INTERESTS]: "Hobbies & Interests",
  [FiveDomain.PHYSICAL_HEALTH]: "Physical Health",
};

export const FIVE_DOMAIN_FACTOR_LABELS: Record<FiveDomainFactor, string> = {
  [FiveDomainFactor.UNDERSTAND_ROLES_AND_RESPONSIBILITIES]: "Roles and Responsibilities",
  [FiveDomainFactor.HOW_SUCCESS_IS_MEASURED]: "How Success is Measured",
  [FiveDomainFactor.GIVING_AND_RECEIVING_FEEDBACK]: "Feedback",
  [FiveDomainFactor.MISSION]: "Mission",
  [FiveDomainFactor.OPERATIONS_ALIGN]: "Operations",
  [FiveDomainFactor.VALUES]: "Values",
  [FiveDomainFactor.POSITIVE_STRESS_INDUCING]: "Stress-Inducing",
  [FiveDomainFactor.PERFORMANCE_BASED]: "Performance-Based",
  [FiveDomainFactor.ACCOUNTABILITY_CENTRIC]: "Accountability-Centric",
  [FiveDomainFactor.RESULT_ORIENTED]: "Result-Oriented",
  [FiveDomainFactor.SENSE_OF_UNITY_IN_RELATIONSHIPS]: "Sense of Unity",
  [FiveDomainFactor.HAVE_HOBBIES_AND_UNDERSTAND_IMPORTANCE]: "Have Hobbies and Understand Importance",
  [FiveDomainFactor.SLEEP]: "Sleep",
  [FiveDomainFactor.DIET]: "Diet",
  [FiveDomainFactor.EXERCISE]: "Exercise",
  [FiveDomainFactor.HYDRATION]: "Hydration",
};

export const getFiveDomainLabel = (domain: FiveDomain) => FIVE_DOMAIN_LABELS[domain] ?? "Unknown Domain";
export const getFiveDomainFactorLabel = (factor: FiveDomainFactor) => FIVE_DOMAIN_FACTOR_LABELS[factor] ?? "Unknown Factor";
